import { DocumentNode } from 'graphql';
import {
  editPatientOobWarningDelayReason,
  editPatientPtWarningDelayReason,
  editPatientPtCompleteWarningDelayReason,
  editPatientPacuOutWarningDelayReason,
} from '@/graph/patients';

/**
 * Single source of truth for PACU milestone delay-reason metadata.
 *
 * The slug `value`s below are persisted verbatim in the database and MUST NOT
 * be renamed. Labels are the human-readable option text. Both are copied
 * verbatim from the active tablet picker (PatientDelayReasonModal).
 */

export type DelayMilestone = 'oob' | 'pt' | 'ptComplete' | 'pacuOut';

export type DelayReasonOption = {
  value: string;
  label: string;
};

/**
 * Result of interpreting a stored/raw delay-reason value against the option
 * list for a milestone. Used to prefill the shared input.
 */
export type ParsedDelayReason =
  | { kind: 'none' }
  | { kind: 'option'; option: DelayReasonOption }
  | { kind: 'other'; custom: string };

/**
 * The literal 'other' slug that toggles the free-text affordance. Kept as a
 * named constant so consumers never hardcode the string.
 */
export const OTHER_REASON_VALUE = 'other';

export const delayReasonOptions: Record<DelayMilestone, DelayReasonOption[]> = {
  oob: [
    { value: 'nausea_or_vomiting', label: 'Nausea or Vomiting' },
    { value: 'sedation', label: 'Sedation' },
    { value: 'spinal', label: 'Spinal' },
    { value: 'pain', label: 'Pain' },
    { value: 'refusal', label: 'Refusal' },
    { value: 'orthostatic', label: 'Orthostatic' },
    { value: 'other', label: 'Other' },
  ],
  pt: [
    { value: 'spinal', label: 'Spinal' },
    { value: 'sedation', label: 'Sedation' },
    { value: 'quad_weakness', label: 'Quad Weakness' },
    { value: 'pain', label: 'Pain' },
    { value: 'n_v', label: 'N/V' },
    { value: 'orthostatic', label: 'Orthostatic' },
    { value: 'other', label: 'Other' },
  ],
  ptComplete: [
    { value: 'comorbidities', label: 'Comorbidities' },
    { value: 'staffing', label: 'Staffing' },
    { value: 'carepartner', label: 'Carepartner' },
    { value: 'spinal', label: 'Spinal' },
    { value: 'sedation', label: 'Sedation' },
    { value: 'pain', label: 'Pain' },
    { value: 'orthostatic', label: 'Orthostatic' },
    { value: 'quad_weakness', label: 'Quad Weakness' },
    { value: 'pour', label: 'POUR' },
    { value: 'lengthy_discharge', label: 'Lengthy discharge' },
    { value: 'other', label: 'Other' },
  ],
  pacuOut: [
    { value: 'pain_management', label: 'Pain Management' },
    { value: 'nausea', label: 'Nausea/Vomiting' },
    { value: 'vital_signs', label: 'Vital Signs Not Stable' },
    { value: 'patient_not_ready', label: 'Patient Not Ready' },
    { value: 'waiting_for_physician', label: 'Waiting for Physician' },
    { value: 'waiting_for_transport', label: 'Waiting for Transport' },
    { value: 'waiting_for_family', label: 'Waiting for Family' },
    { value: 'medical_issue', label: 'Medical Issue' },
    { value: 'other', label: 'Other (Specify)' },
  ],
};

/**
 * Maps a backend patient-event type to its delay milestone. Defined exactly for
 * the four editable PACU milestones; any other type resolves to undefined.
 */
export const milestoneFromEventType: Record<string, DelayMilestone> = {
  TransferredToChair: 'oob',
  TransferredToPT: 'pt',
  PTComplete: 'ptComplete',
  Discharged: 'pacuOut',
};

/**
 * Human-readable milestone names, keyed by the backend `status` string carried
 * on `DelayReasonChange` audit-log entries.
 */
export const milestoneLabels: Record<string, string> = {
  OOB: 'Out of Bed',
  PT: 'Transfer to PT',
  PTComplete: 'PT Complete',
  PacuOut: 'PACU Out',
};

export type DelayReasonMutationConfig = {
  mutation: DocumentNode;
  variableName: string;
  patientField: string;
};

/**
 * Per-milestone GraphQL mutation wiring: which mutation document to call, the
 * name of its reason variable, and the patient field that holds the current
 * value.
 */
export const delayReasonMutationConfig: Record<DelayMilestone, DelayReasonMutationConfig> = {
  oob: {
    mutation: editPatientOobWarningDelayReason,
    variableName: 'oobWarningDelayReason',
    patientField: 'oobWarningDelayReason',
  },
  pt: {
    mutation: editPatientPtWarningDelayReason,
    variableName: 'ptWarningDelayReason',
    patientField: 'ptWarningDelayReason',
  },
  ptComplete: {
    mutation: editPatientPtCompleteWarningDelayReason,
    variableName: 'ptCompleteWarningDelayReason',
    patientField: 'ptCompleteWarningDelayReason',
  },
  pacuOut: {
    mutation: editPatientPacuOutWarningDelayReason,
    variableName: 'pacuOutWarningDelayReason',
    patientField: 'pacuOutWarningDelayReason',
  },
};

/**
 * Interpret a raw stored value for prefilling the input:
 * - matches an option slug -> that option
 * - non-empty, non-matching -> custom free text under the 'other' affordance
 * - empty / null / undefined -> none
 */
/**
 * Resolves a stored delay-reason value to its human-readable label for display
 * in the audit log. Matches against every milestone's option list (labels are
 * consistent across lists for shared slugs) and falls back to the raw value for
 * custom / legacy free-text reasons.
 */
export const delayReasonLabelForValue = (value: string): string => {
  for (const options of Object.values(delayReasonOptions)) {
    const match = options.find(option => option.value === value);
    if (match) {
      return match.label;
    }
  }
  return value;
};

export const parseDelayReasonValue = (delayType: DelayMilestone, raw: string | null | undefined): ParsedDelayReason => {
  if (!raw) {
    return { kind: 'none' };
  }
  const match = delayReasonOptions[delayType].find(option => option.value === raw);
  if (match) {
    return { kind: 'option', option: match };
  }
  return { kind: 'other', custom: raw };
};

/** Backend bucket for delayed cases with no stored reason. */
export const NO_REASON_RECORDED = 'No reason recorded';

/** Chart slice label for every custom free-text reason entered via "Other". */
export const OTHER_CHART_LABEL = 'Other';

export type DelayReasonChartInput = {
  reason: string;
  count: number;
  avgMinutes?: number | null;
};

export type DelayReasonChartBreakdown = {
  reason: string;
  count: number;
};

export type DelayReasonChartSlice = DelayReasonChartInput & {
  breakdown?: DelayReasonChartBreakdown[];
};

const matchesOption = (option: DelayReasonOption, raw: string): boolean => {
  if (option.value === OTHER_REASON_VALUE) {
    return false;
  }
  return option.value === raw || option.label.toLowerCase() === raw.toLowerCase();
};

/**
 * Resolves a stored analytics reason to a preset option. Prefers this
 * milestone's list, then any PACU milestone, matching slug or label.
 */
const findPresetOption = (delayType: DelayMilestone, raw: string): DelayReasonOption | undefined => {
  const trimmed = raw.trim();
  const preferred = delayReasonOptions[delayType].find(option => matchesOption(option, trimmed));
  if (preferred) {
    return preferred;
  }
  for (const options of Object.values(delayReasonOptions)) {
    const match = options.find(option => matchesOption(option, trimmed));
    if (match) {
      return match;
    }
  }
  return undefined;
};

const isOtherStoredValue = (raw: string): boolean => {
  const trimmed = raw.trim().toLowerCase();
  return trimmed === OTHER_REASON_VALUE || trimmed === 'other (specify)';
};

const weightedAverageMinutes = (items: DelayReasonChartInput[]): number | null => {
  let weightedSum = 0;
  let weightedCount = 0;
  for (const item of items) {
    if (item.avgMinutes != null && Number.isFinite(item.avgMinutes) && item.count > 0) {
      weightedSum += item.avgMinutes * item.count;
      weightedCount += item.count;
    }
  }
  return weightedCount > 0 ? weightedSum / weightedCount : null;
};

/**
 * Collapses custom "Other" free-text reasons into a single chart slice.
 * Preset options keep their labels. Average minutes on Other is the weighted
 * average of every custom reason in that bucket; per-reason counts go in
 * `breakdown` for the tooltip.
 */
export const groupDelayReasonsForChart = (
  reasons: DelayReasonChartInput[],
  delayType: DelayMilestone
): DelayReasonChartSlice[] => {
  const buckets = new Map<string, DelayReasonChartInput[]>();

  for (const item of reasons) {
    const trimmed = item.reason.trim();
    let key: string;
    if (!trimmed || trimmed === NO_REASON_RECORDED) {
      key = NO_REASON_RECORDED;
    } else if (isOtherStoredValue(trimmed)) {
      key = OTHER_CHART_LABEL;
    } else {
      const preset = findPresetOption(delayType, trimmed);
      key = preset ? preset.label : OTHER_CHART_LABEL;
    }
    const list = buckets.get(key) ?? [];
    list.push(item);
    buckets.set(key, list);
  }

  return [...buckets.entries()]
    .map(([reason, items]) => {
      const count = items.reduce((sum, item) => sum + item.count, 0);
      const avgMinutes = weightedAverageMinutes(items);
      if (reason !== OTHER_CHART_LABEL) {
        return { reason, count, avgMinutes };
      }
      const breakdownCounts = new Map<string, number>();
      for (const item of items) {
        const label = isOtherStoredValue(item.reason) ? OTHER_CHART_LABEL : item.reason.trim();
        breakdownCounts.set(label, (breakdownCounts.get(label) ?? 0) + item.count);
      }
      const breakdown = [...breakdownCounts.entries()]
        .map(([customReason, customCount]) => ({ reason: customReason, count: customCount }))
        .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
      const hasCustomBreakdown = breakdown.some(item => item.reason !== OTHER_CHART_LABEL);
      return {
        reason,
        count,
        avgMinutes,
        ...(hasCustomBreakdown ? { breakdown } : {}),
      };
    })
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
};
